import { useEffect, useMemo, useState } from 'react';

import { GetDeploymentRulesRequestDTO, MODULE } from '@gearbelt/global';
import { Button, buttonVariants } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { Link, useLocation } from 'react-router-dom';
import { z } from 'zod';

import deploymentVector from '@/assets/vectors/deployment.svg';
import SearchBar from '@/components/common/search-bar';
import { FeedbackPrompt } from '@/components/feedback/feedback-prompt';
import DeleteIcon from '@/components/icons/delete';
import LoadingIcon from '@/components/icons/loading';
import PencilIcon from '@/components/icons/pencil';
import PlusIcon from '@/components/icons/plus';
import QuestionIcon from '@/components/icons/question';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { useToast } from '@/components/ui/toaster';
import { useDeploymentRecalcRun } from '@/hooks/use-deployment-recalc-run';
import { useFeedbackPrompt } from '@/hooks/use-feedback-prompt';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { cn } from '@/lib/utils';
import { deploymentRuleKeys, getDeploymentRulesInfiniteQueryOptions } from '@/queries/deployments';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { getWorkspaceRoute, useWorkspaceNavigate } from '@/utils/routes';

import { DeleteConfirmationDialog } from '../../components/common/delete-confirmation-dialog';
import { createDeploymentTableColumns } from './table-meta';

const DeploymentLocationStateSchema = z.object({
  showFeedback: z.boolean().optional(),
  deploymentRuleId: z.string().optional(),
  deploymentRuleName: z.string().optional(),
  recalcRunId: z.string().optional(),
});

export function DeploymentsPage() {
  const queryClient = useQueryClient();
  const navigate = useWorkspaceNavigate();
  const { syncModuleColumns } = useModulesStore();
  const toast = useToast();
  const { canManageDeploymentRules } = usePermissions();
  const {
    config: { sorting },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.DeploymentRules, {
    columns: createDeploymentTableColumns({ canManageDeploymentRules }),
  });

  const location = useLocation();
  const { trigger: triggerFeedback, promptProps: feedbackPromptProps } = useFeedbackPrompt('deployment-rules');

  const locationState = DeploymentLocationStateSchema.safeParse(location.state).data;
  const deploymentRuleId = locationState?.deploymentRuleId;
  const deploymentRuleName = locationState?.deploymentRuleName;

  // Snapshot the run id once — the feedback effect below clears history state
  // on mount, and polling must survive re-renders after that.
  const [recalcRunId] = useState(locationState?.recalcRunId);
  const { isRecalculating, failed: recalcFailed, progress: recalcProgress } = useDeploymentRecalcRun(recalcRunId);

  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: deploymentRules,
    fetchNextPage: fetchNextDeploymentRulesPage,
    hasNextPage: hasNextDeploymentRulesPage,
    isFetching: isFetchingDeploymentRules,
    isFetchingNextPage: isFetchingNextDeploymentRulesPage,
    error: deploymentRulesError,
  } = useInfiniteQuery(
    getDeploymentRulesInfiniteQueryOptions({
      search: debouncedSearch,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetDeploymentRulesRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedDeploymentRules = useMemo(() => {
    return (incomingData: typeof deploymentRules) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const deploymentRules = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => deploymentRules[parseInt(id)]);
    };
  }, [selectedRows]);

  const selectedDeploymentRules = useMemo(() => getSelectedDeploymentRules(deploymentRules), [deploymentRules, getSelectedDeploymentRules]);

  const { mutate: deleteDeploymentRules, isPending: isDeletingDeploymentRules } = useMutation({
    mutationFn: async () => await ApiService.DeleteDeploymentRules({ deploymentRuleIds: selectedDeploymentRules.map((rule) => rule.id) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentRuleKeys.all });
      toast.success('Rules deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});
    },
    onError: () => {
      toast.error('Failed to delete deployment rules');
    },
  });

  function handleEditDeploymentRule(deploymentRuleId: string) {
    navigate(`/deployments/${deploymentRuleId}`);
  }

  function handleDeleteDeploymentRules() {
    deleteDeploymentRules();
  }

  useEffect(() => {
    if (locationState?.showFeedback) {
      window.history.replaceState({}, '');
      triggerFeedback({ deploymentRuleId, deploymentRuleName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount; locationState + triggerFeedback are stable references and the modal must not re-fire on re-renders
  }, []);

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.DeploymentRules, createDeploymentTableColumns({ canManageDeploymentRules }));
  }, [syncModuleColumns, canManageDeploymentRules]);

  useEffect(() => {
    if (deploymentRulesError) {
      toast.error(deploymentRulesError.message);
    }
  }, [toast, deploymentRulesError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, sorting.key, sorting.direction]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextDeploymentRulesPage,
    hasNextPage: hasNextDeploymentRulesPage,
    isFetching: isFetchingDeploymentRules,
    isError: !!deploymentRulesError,
  });

  const flattenedData = useMemo(() => deploymentRules?.pages.flatMap((page) => page.data) ?? [], [deploymentRules]);

  return (
    <>
      <div className="bg-grey-1 flex flex-1 flex-col px-6">
        <div className="mx-auto flex h-full w-full flex-1 flex-col gap-4 py-6">
          <div className="flex h-full flex-col gap-4 rounded-md bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-medium">Deployment Rules</h3>
              <Link
                to="https://help.sitemate.com/en/articles/11063127-how-to-create-deployment-rules-in-gearbelt"
                target="_blank"
                className="bg-grey-1 inline-flex w-auto gap-1 rounded p-1 text-sm font-medium">
                <QuestionIcon className="h-4 w-4" />
                Learn more
              </Link>
            </div>
            <div className="text-sm">Deployment rules automatically deploy forms to your assets based on their properties.</div>
            {isRecalculating && (
              <div className="flex items-center gap-2">
                <span className="text-secondary-text text-sm">Updating deployed forms and schedules…</span>
                <RecalcProgressIndicator progress={recalcProgress} />
              </div>
            )}
            {recalcFailed && <span className="text-default-red text-sm">Deploying forms and schedules failed — re-save the rule to retry.</span>}
            {canManageDeploymentRules && (
              <Link
                to={getWorkspaceRoute('/deployments/create')}
                className={cn(buttonVariants({ variant: 'success' }), 'flex w-fit items-center gap-1 px-2 font-medium')}>
                <PlusIcon />
                <span>Create Rule</span>
              </Link>
            )}
            <div>
              <div className="border-grey-3 flex flex-1 flex-col rounded-t border-x border-y">
                <div className="bg-grey-2 flex flex-shrink-0 items-center gap-2 overflow-x-hidden rounded-t px-2 py-1">
                  {selectedDeploymentRules.length === 0 && <SearchBar className="w-[300px]" search={search} setSearch={setSearch} />}
                  {canManageDeploymentRules && selectedDeploymentRules.length === 1 && (
                    <Button
                      variant="ghost"
                      icon={<PencilIcon />}
                      className="bg-grey-1 hover:bg-grey-3 px-2"
                      onClick={() => handleEditDeploymentRule(selectedDeploymentRules[0].id)}>
                      Edit
                    </Button>
                  )}
                  {canManageDeploymentRules && selectedDeploymentRules.length > 0 && (
                    <Button
                      variant="ghost"
                      icon={<DeleteIcon className="text-default-red" />}
                      className="bg-grey-1 hover:bg-grey-3 px-2"
                      onClick={() => setOpenDeleteConfirmation(true)}>
                      Delete
                    </Button>
                  )}
                </div>
                <div className="scrollbar scrollbar-track-transparent scrollbar-thumb-grey-3 relative overflow-x-auto border-t">
                  <TablePageTable
                    columns={createDeploymentTableColumns({ canManageDeploymentRules })}
                    data={flattenedData || []}
                    loading={(isFetchingDeploymentRules && !isFetchingNextDeploymentRulesPage) || isLoadingPageConfig}
                    tableClassName="table-fixed [&_tr:last-child_td]:border-b-0 min-w-[1024px]"
                    cellClassName="border-x-transparent"
                    headClassName="!border-none"
                    headerClassName="after:!hidden before:!hidden"
                    sortButtonClassName="justify-start"
                    onRowSelectionChange={setSelectedRows}
                    rowSelection={selectedRows}
                    initialSorting={initialSorting}
                    onSortingChange={(sorting) =>
                      updatePageConfig({
                        table: { sorting },
                      })
                    }
                    onRowClick={(row) => {
                      // /deployments/:id is the editor — Members have view-only
                      // access via the register row, not the edit form.
                      if (!canManageDeploymentRules) return;
                      handleEditDeploymentRule(row.id);
                    }}
                    notFoundDisplay={
                      <NoDeploymentRulesFoundDisplay description={search ? 'No deployment rules found' : 'No deployment rules have been created yet'} />
                    }
                  />
                </div>
                {hasNextDeploymentRulesPage && (
                  <div className="border-grey-3 w-full border">
                    <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
                      {isFetchingNextDeploymentRulesPage ? 'Loading more...' : 'Load Newer'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteDeploymentRules}
        loading={isDeletingDeploymentRules}
        title="Delete this rule?"
        description="Are you sure you want to delete this deployment rule? This action is permanent and cannot be undone."
      />

      <FeedbackPrompt {...feedbackPromptProps} />
    </>
  );
}

function RecalcProgressIndicator({ progress }: { progress: number }) {
  return (
    <div className="flex items-center gap-2 px-1" role="status" aria-live="polite" aria-label={`Updating deployments, ${progress}%`}>
      <LoadingIcon size={18} />
      <div className="bg-grey-3 h-1.5 w-24 overflow-hidden rounded-full">
        {/* Inline width is the documented exception for dynamic percentages. */}
        <div className="bg-default-blue h-full rounded-full transition-[width] duration-300 ease-out" style={{ width: `${progress}%` }} />
      </div>
      <span className="text-secondary-text w-8 text-xs tabular-nums">{progress}%</span>
    </div>
  );
}

function NoDeploymentRulesFoundDisplay({ description }: { description?: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white px-4 py-16">
      <img src={deploymentVector} alt="Deployment" className="h-28" />
      <span className="text-xl leading-[30px] font-medium">{description}</span>
    </div>
  );
}
